// app.files — blob/file storage (photos, uploads, attachments) on the project's
// S3 bucket, distinct from app.collection/doc/log (s3db structured data). Even
// apps that keep structured data in an external database usually want this.
//
// Objects live under `files/`, with the SAME scopes as the database (s3db):
// `private` (default) → your own `files/private/{sub}/…` (gated by the
// `${jwt:sub}` IAM floor); `shared` → `files/shared/…` (every member reads;
// writing needs an app-role grant beyond the floor); `admin` → `files/admin/…`
// (only roles holding `admin-data` / `read-all`). NOTE: no scope is served
// over HTTP — even `shared` is member-only, reachable through STS.
//
// Dependency-free: uses the same STS-scoped S3 client as the rest of the SDK —
// no presigner package. `objectUrl()` fetches the bytes and wraps them in a
// `blob:` URL (handy for <img src>); a presigned-URL variant can come later.

import {
  DeleteObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { paths } from "./paths.ts";

/** Where a file lives — the same scope ladder as the database. */
export type FileScope = "private" | "shared" | "admin";

export interface FilePutOptions {
  contentType?: string;
  /** "private" (default) → `files/private/{sub}/…`; "shared" →
   *  `files/shared/…`; "admin" → `files/admin/…`. */
  scope?: FileScope;
}

export interface FileEntry {
  /** Full S3 key. */
  key: string;
  size: number;
  lastModified?: string;
}

export interface FilesModule {
  /** Upload a blob. Returns its full S3 key. */
  put(
    key: string,
    body: Blob | Uint8Array | string,
    opts?: FilePutOptions,
  ): Promise<{ key: string }>;
  /** Download a blob's bytes. */
  get(key: string, opts?: { scope?: FileScope }): Promise<Uint8Array>;
  /** Fetch + wrap in a `blob:` URL for `<img src>`. The caller is responsible
   *  for `URL.revokeObjectURL` when done. */
  objectUrl(
    key: string,
    opts?: { scope?: FileScope; type?: string },
  ): Promise<string>;
  /** List files under a prefix within the chosen scope (default: your own). */
  list(prefix?: string, opts?: { scope?: FileScope }): Promise<FileEntry[]>;
  /** Delete a file. */
  delete(key: string, opts?: { scope?: FileScope }): Promise<void>;
}

export interface FilesConfig {
  bucket: string;
  getClient: () => Promise<S3Client>;
  getSub: () => string | null;
}

export function createFiles(cfg: FilesConfig): FilesModule {
  /** The prefix for a scope (see paths.ts). */
  function base(scope: FileScope): string {
    if (scope === "shared") return paths().filesShared();
    if (scope === "admin") return paths().filesAdmin();
    const sub = cfg.getSub();
    if (!sub) throw new Error("app.files: not signed in");
    return paths().filesPrivate(sub);
  }
  function resolve(key: string, scope: FileScope): string {
    return base(scope) + key.replace(/^\/+/, "");
  }

  async function getBytes(key: string, scope: FileScope): Promise<Uint8Array> {
    const s3 = await cfg.getClient();
    const out = await s3.send(
      new GetObjectCommand({ Bucket: cfg.bucket, Key: resolve(key, scope) }),
    );
    const body = out.Body as
      | { transformToByteArray: () => Promise<Uint8Array> }
      | undefined;
    if (!body) throw new Error(`app.files: empty body for '${key}'`);
    return body.transformToByteArray();
  }

  return {
    async put(key, body, opts = {}) {
      const s3 = await cfg.getClient();
      const Key = resolve(key, opts.scope ?? "private");
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key,
          Body: body,
          ...(opts.contentType ? { ContentType: opts.contentType } : {}),
        }),
      );
      return { key: Key };
    },
    get(key, opts = {}) {
      return getBytes(key, opts.scope ?? "private");
    },
    async objectUrl(key, opts = {}) {
      const bytes = await getBytes(key, opts.scope ?? "private");
      const blob = new Blob([bytes], opts.type ? { type: opts.type } : undefined);
      return URL.createObjectURL(blob);
    },
    async list(prefix = "", opts = {}) {
      const s3 = await cfg.getClient();
      const Prefix = base(opts.scope ?? "private") + prefix.replace(/^\/+/, "");
      const res = await s3.send(
        new ListObjectsV2Command({ Bucket: cfg.bucket, Prefix }),
      );
      return (res.Contents ?? []).map((o) => ({
        key: o.Key ?? "",
        size: o.Size ?? 0,
        lastModified: o.LastModified?.toISOString(),
      }));
    },
    async delete(key, opts = {}) {
      const s3 = await cfg.getClient();
      await s3.send(
        new DeleteObjectCommand({
          Bucket: cfg.bucket,
          Key: resolve(key, opts.scope ?? "private"),
        }),
      );
    },
  };
}
